export enum CareerStage {
  Entry = 'entry',
  Mid = 'mid',
  Senior = 'senior',
  Transition = 'transition'
}

export interface LearningPath {
  title: string;
  description: string;
  milestones: string[];
}

export interface OnboardingState {
  currentStage: string;
  completedStages: string[];
  userResponses: OnboardingResponses;
  totalPoints: number;
}

export interface OnboardingResponses {
  basicInfo: {
    experience: string;
    currentRole: string;
    education: string;
    certifications: string[];
    industries: string[];
    projectHighlights: string[];
  };
  careerGoals: {
    shortTerm: string;
    longTerm: string;
    industry: string;
    desiredRoles: string[];
    salary: {
      current: number;
      desired: number;
    };
    workPreferences: {
      remote: boolean;
      travel: string;
      workLife: string;
    };
  };
  skills: {
    technical: string[];
    soft: string[];
    leadership: string[];
    domain: string[];
    tools: string[];
    methodologies: string[];
  };
  preferences: {
    workStyle: string;
    learningPreference: string;
    timeCommitment: string;
    teamSize: string;
    companySize: string;
    companyStage: string;
    mentorship: string;
    projectTypes: string[];
  };
  interests: {
    industries: string[];
    technologies: string[];
    causes: string[];
    roles: string[];
  };
}

export interface AssessmentResponse {
  skills: SkillAssessment[];
  experience: ExperienceDetails;
  interests: InterestArea[];
  goals: CareerGoal[];
}

export interface SkillAssessment {
  name: string;
  level: number;
  category: string;
  yearsOfExperience: number;
}

export interface ExperienceDetails {
  totalYears: number;
  currentRole: string;
  previousRoles: string[];
  industries: string[];
}

export interface InterestArea {
  category: string;
  items: string[];
  priority: number;
}

export type OnboardingStage = 
  | 'WELCOME'
  | 'CAREER_GOALS'
  | 'SKILLS_ASSESSMENT'
  | 'LEARNING_STYLE'
  | 'PERSONALITY'
  | 'AI_ASSESSMENT'
  | 'RECOMMENDATIONS'
  | 'COMPLETE';

export interface Assessment {
  status: 'pending' | 'in_progress' | 'complete';
  sections: {
    goals: boolean;
    skills: boolean;
    initial: boolean;
    personality: boolean;
  };
  responses: {
    skills: string[];
    initial: InitialAssessment;
    career_goals: CareerGoal[];
  };
}

export interface InitialAssessment {
  career_stage: CareerStage;
  years_experience: number;
  current_role: string;
  desired_role: string;
  biggest_challenges: string[];
  learning_goals: string[];
  preferred_learning_methods: string[];
  time_commitment: 'minimal' | 'moderate' | 'intensive';
  skill_level: 'beginner' | 'intermediate' | 'advanced';
}

export interface UserPreferences {
  learning_style: string[];
  career_interests: string[];
  work_environment: string[];
  skill_focus: string[];
}

export interface CareerGoal {
  id: string;
  title: string;
  timeframe: string;
  priority: 'low' | 'medium' | 'high';
  status: 'not_started' | 'in_progress' | 'completed';
} 